import { useEffect, useCallback } from 'react'

type KeyModifier = 'ctrl' | 'alt' | 'shift' | 'meta'
type KeyCombo = {
  key: string
  modifiers?: KeyModifier[]
}

interface UseKeyboardShortcutOptions {
  enabled?: boolean
  preventDefault?: boolean
}

/**
 * Hook to handle keyboard shortcuts
 * @param keyCombo - The key combination to listen for
 * @param callback - Function to call when shortcut is triggered
 * @param options - Additional options
 */
export function useKeyboardShortcut(
  keyCombo: KeyCombo | KeyCombo[],
  callback: (event: KeyboardEvent) => void,
  options: UseKeyboardShortcutOptions = {}
) {
  const { enabled = true, preventDefault = true } = options

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return

      const combos = Array.isArray(keyCombo) ? keyCombo : [keyCombo]

      for (const combo of combos) {
        const keyMatches = event.key.toLowerCase() === combo.key.toLowerCase()
        if (!keyMatches) continue

        const modifiers = combo.modifiers || []
        const ctrlRequired = modifiers.includes('ctrl')
        const altRequired = modifiers.includes('alt')
        const shiftRequired = modifiers.includes('shift')
        const metaRequired = modifiers.includes('meta')

        // Check if required modifiers are pressed and no extra modifiers
        const ctrlMatches = ctrlRequired === event.ctrlKey
        const altMatches = altRequired === event.altKey
        const shiftMatches = shiftRequired === event.shiftKey
        const metaMatches = metaRequired === event.metaKey

        if (ctrlMatches && altMatches && shiftMatches && metaMatches) {
          if (preventDefault) {
            event.preventDefault()
          }
          callback(event)
          return
        }
      }
    },
    [keyCombo, callback, enabled, preventDefault]
  )

  useEffect(() => {
    if (!enabled) return

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown, enabled])
}

/**
 * Detect if user is on macOS
 */
export function isMac(): boolean {
  if (typeof navigator === 'undefined') return false
  return navigator.platform?.toLowerCase().includes('mac') ||
         navigator.userAgent?.toLowerCase().includes('mac')
}

/**
 * Get display string for keyboard shortcut
 */
export function getShortcutDisplay(modifiers: KeyModifier[], key: string): string {
  const mac = isMac()
  const modifierSymbols: Record<KeyModifier, string> = mac
    ? { ctrl: '⌃', alt: '⌥', shift: '⇧', meta: '⌘' }
    : { ctrl: 'Ctrl', alt: 'Alt', shift: 'Shift', meta: 'Win' }

  const parts = modifiers.map((m) => modifierSymbols[m])
  parts.push(key.toUpperCase())

  return mac ? parts.join('') : parts.join('+')
}
